import React from 'react';
import { PublicLayout } from '../../components/PublicLayout';
import { PageHero, BenefitsGrid, FAQSection, CTASection, FeatureList } from '../../components/PageTemplate';
import { 
  Shield, CheckCircle, User, Briefcase, Home, 
  CreditCard, Clock, Eye 
} from 'lucide-react';

export default function TenantsReferencing() {
  const whatWeCheck = [
    {
      icon: User,
      title: 'Identity Verification',
      description: 'Passport or driving license checked against official databases to confirm you are who you say you are.'
    },
    {
      icon: Shield,
      title: 'Right to Rent',
      description: 'Confirmation that you have the legal right to rent in the UK.'
    },
    {
      icon: Briefcase,
      title: 'Employment Check',
      description: 'We contact your employer to verify your role, length of service, and income.'
    },
    {
      icon: CreditCard,
      title: 'Credit Check',
      description: 'A soft check (doesn\'t affect your credit score) to review your financial history.'
    },
    {
      icon: Home,
      title: 'Previous Landlord',
      description: 'Reference from your current or previous landlord about your tenancy conduct.'
    },
    {
      icon: CheckCircle,
      title: 'Affordability',
      description: 'Calculation to ensure the rent is affordable based on your income.'
    }
  ];

  const timeline = [
    { stage: 'Application Submitted', time: 'Day 1' },
    { stage: 'ID Verified', time: 'Day 1' },
    { stage: 'Employer Contacted', time: 'Day 1-2' },
    { stage: 'Credit Check Run', time: 'Day 1' },
    { stage: 'Landlord Reference Requested', time: 'Day 1-3' },
    { stage: 'All References Received', time: 'Day 2-5' },
    { stage: 'Decision Made', time: 'Day 2-5' },
  ];

  const faqs = [
    {
      question: 'Will this affect my credit score?',
      answer: 'No. We perform a "soft" credit check which doesn\'t appear on your credit file and doesn\'t affect your credit score.'
    },
    {
      question: 'What if I\'m self-employed?',
      answer: 'Instead of employer verification, we\'ll ask for your last 2-3 years of accounts or tax returns, and may request an accountant reference.'
    },
    {
      question: 'What if I don\'t have a previous landlord?',
      answer: 'First-time renters can provide alternative references (e.g., university accommodation, parental home confirmation) or use a guarantor.'
    },
    {
      question: 'What if my income is below the threshold?',
      answer: 'Options include: a guarantor, paying 6+ months rent upfront, or providing evidence of additional income/savings.'
    },
    {
      question: 'How long does referencing take?',
      answer: 'Typically 24-72 hours. It depends on how quickly your employer and previous landlord respond to our requests.'
    },
    {
      question: 'Can I see what you\'ve found?',
      answer: 'Yes. You have access to a summary of your referencing results. If something is incorrect, you can raise a dispute.'
    }
  ];

  return (
    <PublicLayout>
      <PageHero
        badge="REFERENCING"
        title="Transparent"
        subtitle="Referencing"
        description="Know exactly what we check and why. Fast, fair, and doesn't affect your credit score."
        primaryCTA={{ href: '/register', label: 'Get Started' }}
        secondaryCTA={{ href: '/tenants', label: 'Back to Overview' }}
        gradient="from-teal-50 via-white to-slate-50"
      />

      <BenefitsGrid title="What We Check" benefits={whatWeCheck} />

      {/* Timeline */}
      <section className="py-12 sm:py-16 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <h2 className="text-2xl sm:text-3xl font-display font-bold text-center mb-8">
            Typical Timeline
          </h2>
          <div className="space-y-4">
            {timeline.map((item, index) => (
              <div key={index} className="flex items-center gap-4">
                <div className="w-16 text-right text-sm font-medium text-slate-500">{item.time}</div>
                <div className="w-3 h-3 rounded-full bg-primary"></div>
                <div className="flex-1 py-3 px-4 bg-slate-50 rounded-lg text-sm font-medium">
                  {item.stage}
                </div>
              </div>
            ))}
          </div>
          <p className="text-center text-sm text-slate-500 mt-6">
            * Timings depend on how quickly third parties respond
          </p>
        </div>
      </section>

      {/* Transparency Promise */}
      <section className="py-12 sm:py-16 bg-slate-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <div className="bg-white rounded-2xl p-6 sm:p-8 shadow-sm">
            <div className="flex items-center gap-3 mb-4">
              <Eye className="w-8 h-8 text-primary" />
              <h2 className="text-xl sm:text-2xl font-bold">Our Transparency Promise</h2>
            </div>
            <FeatureList features={[
              'You\'ll know exactly what checks we run before we run them',
              'You can see a summary of your referencing results',
              'If something is wrong, you can dispute it with evidence',
              'We don\'t share your data with anyone except the landlord you\'re applying to',
              'Your credit score is never affected by our checks'
            ]} />
          </div>
        </div>
      </section>

      <FAQSection faqs={faqs} />

      <CTASection
        title="Ready to get referenced?"
        description="Create your profile and apply to properties. Referencing starts automatically."
        primaryCTA={{ href: '/register', label: 'Create Profile' }}
      />
    </PublicLayout>
  );
}
